import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  locationRequestsPath,
  newLocationRequestPath,
  locationRequestPath,
} from "@/routes";
import { Choice } from "./controls";
import type { Enquiry, Pagination } from "./types";

export default function RequestsIndex({
  enquiries,
  pagination,
  filters,
}: {
  enquiries: Enquiry[];
  pagination: Pagination;
  filters: { q?: string; state?: string };
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const path = locationRequestsPath(locationId);
  return (
    <AppLayout breadcrumbs={[{ title: t("requests.title"), href: path }]}>
      <Head title={t("requests.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-page font-display">{t("requests.title")}</h1>
            <p className="text-muted-foreground">{auth.location.name}</p>
          </div>
          <Button render={<Link href={newLocationRequestPath(locationId)} />}>
            {t("requests.add")}
          </Button>
        </div>
        <Form
          action={path}
          method="get"
          className="grid items-end gap-3 sm:grid-cols-[1fr_12rem_auto]"
        >
          <div className="space-y-2">
            <Label htmlFor="request-search">{t("requests.search")}</Label>
            <Input id="request-search" name="q" defaultValue={filters.q} />
          </div>
          <Choice
            id="request-state-filter"
            name="state"
            label={t("requests.state")}
            defaultValue={filters.state || "all"}
            options={[
              { id: "all", name: t("requests.all") },
              ...[
                "new",
                "responded",
                "accepted",
                "declined",
                "rescheduled",
              ].map((id) => ({ id, name: t(`requests.states.${id}`) })),
            ]}
          />
          <Button variant="outline" type="submit">
            {t("requests.filter")}
          </Button>
        </Form>
        {enquiries.length === 0 && (
          <p className="text-muted-foreground">{t("requests.empty")}</p>
        )}
        <ul className="space-y-3">
          {enquiries.map((enquiry) => (
            <li key={enquiry.id}>
              <Card>
                <CardContent className="space-y-2 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link
                      className="font-medium underline underline-offset-4"
                      href={locationRequestPath(locationId, enquiry.id)}
                    >
                      {enquiry.customer?.name ||
                        (enquiry.channel === "whatsapp"
                          ? t("whatsapp.enquiry")
                          : enquiry.channel === "instagram"
                            ? t("instagram.enquiry")
                            : enquiry.channel_identity) ||
                        t("requests.unnamed")}
                    </Link>
                    <span className="rounded-md border px-2 py-1 text-sm">
                      {t(`requests.states.${enquiry.state}`)}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t(`requests.channels.${enquiry.channel}`)} ·{" "}
                    {t(
                      enquiry.complete
                        ? "requests.complete"
                        : "requests.incomplete",
                    )}
                  </p>
                  <p className="line-clamp-2 break-words">{enquiry.body}</p>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
        <nav
          aria-label={t("requests.pages")}
          className="flex flex-wrap items-center gap-4"
        >
          {pagination.page > 1 && (
            <Link
              className="underline"
              href={locationRequestsPath(locationId, {
                ...filters,
                page: pagination.page - 1,
              })}
            >
              {t("requests.previous")}
            </Link>
          )}
          <span>{t("requests.page", pagination)}</span>
          {pagination.page < pagination.pages && (
            <Link
              className="underline"
              href={locationRequestsPath(locationId, {
                ...filters,
                page: pagination.page + 1,
              })}
            >
              {t("requests.next")}
            </Link>
          )}
        </nav>
      </PageCanvas>
    </AppLayout>
  );
}
